import { PATH } from 'constants/path';
import dayjs from 'dayjs';
import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    EAppointmentType,
    EMessageSenderType,
    EMessageType,
    ETextVisitSenderType,
    ETextVisitType,
    TConversation,
    TMessage,
    ETextVisitConditionMap,
    ESubCategory,
    TSupportMessage,
} from 'types/inbox';
import { getTimeStr } from 'utils/calendar';
import AvaIcon from 'assets/common/ava.png';
import s from './s.module.less';
import { getCalendarAppointmentEventDetail } from 'api/google';
import { TServerCalendarAppointmentEventDetail } from 'types/calendar';
import { Button, Spin, Image } from 'antd';
import { TextVisitServiceType } from 'types/textVisit';
import ReviewMessage from './ReviewMessage';
import { getProviderPrivateFileSigned } from 'api/common';
import PreviewImageModal from 'components/PreviewImageModal';
import { getInfoFromConversation } from 'utils/inbox';
import { EChargeType, EChargeTypeLabel } from 'types/provider';
import { getPrivateFilePreview } from 'api/operation';
import { sanitizeRichHtml } from 'utils/sanitize';

type Props = {
    inboxConversation?: TConversation;
    inboxMessage?: TMessage;
    type: 'PATIENT' | 'PROVIDER' | 'OPS';
    onReviewReplySuccess?: () => void;
    onViewTextVisit?: (textVisitUuid: string) => void;
    onAcceptTextVisit?: (textVisitUuid: string, openChargeAfterSubmit?: boolean) => void;
    onViewVideoVisit?: (appointmentId: number) => void;
    timezone?: string;
    blockOpenTextVisitModal?: boolean;
};

const OpsInboxMessage = (props: Props) => {
    const {
        inboxMessage,
        inboxConversation,
        type,
        onViewTextVisit,
        onAcceptTextVisit,
        onReviewReplySuccess,
        onViewVideoVisit,
        timezone,
        blockOpenTextVisitModal,
    } = props;
    const navigate = useNavigate();
    const [loading, setLoading] = useState(false);
    const [isLoadingFile, setIsLoadingFile] = useState(false);
    const [fileUrl, setFileUrl] = useState<string>();
    const [previewVisible, setPreviewVisible] = useState(false);
    const rightToLeft = inboxMessage?.senderType === type;
    const showName = inboxMessage?.senderType !== type;
    const isNewTextVisitProviderCreated =
        inboxMessage?.messageType === EMessageType.TEXT_VISIT &&
        inboxMessage?.textVisit?.type === ETextVisitType.NEW_TEXT_VISIT_PROVIDER_CREATED;
    let medicationName = '';
    if (isNewTextVisitProviderCreated) {
        try {
            const medications = JSON.parse(inboxMessage?.textVisit?.medications || '[]');
            medicationName = medications.join(', ');
        } catch (error) {
            console.error(error);
        }
    }
    let firstName = '';
    let lastName = '';
    const inboxConversationInfo = getInfoFromConversation(inboxConversation);
    if (inboxMessage?.senderType === 'PATIENT' || inboxMessage?.senderType === 'KLARITY_USER') {
        firstName = inboxConversationInfo?.patientFirstName || '';
        lastName = inboxConversationInfo?.patientLastName || '';
    } else if (inboxMessage?.senderType === 'PROVIDER') {
        firstName = inboxConversationInfo?.providerFirstName || '';
        lastName = inboxConversationInfo?.providerLastName || '';
    } else if (inboxMessage?.senderType === 'OPS') {
        firstName = inboxConversationInfo?.opsFirstName || '';
        lastName = inboxConversationInfo?.opsLastName || '';
    } else if (inboxMessage?.messageType === EMessageType.KLARITY_SUPPORT) {
        firstName = inboxConversationInfo?.patientFirstName || '';
        lastName = inboxConversationInfo?.patientLastName || '';
    } else {
        firstName = 'System';
        lastName = '';
    }

    const getChargeDetailText = (item: TSupportMessage) => {
        let ret = '';
        if (item) {
            ret = 'Charge details:';
        }
        const textMap: Partial<Record<EChargeType, EChargeTypeLabel>> = {
            [EChargeType.NO_SHOW]: EChargeTypeLabel.NO_SHOW,
            [EChargeType.LATE_CANCELLATION]: EChargeTypeLabel.LATE_CANCELLATION,
            [EChargeType.RESCHEDULE]: EChargeTypeLabel.RESCHEDULE,
            [EChargeType.LEAD_GEN_FEE]: EChargeTypeLabel.LEAD_GEN_FEE,
            [EChargeType.PARTIAL_PAYMENT]: EChargeTypeLabel.PARTIAL_PAYMENT,
            [EChargeType.FULL_SELF_PAYMENT]: EChargeTypeLabel.FULL_SELF_PAYMENT,
            [EChargeType.CO_PAY]: EChargeTypeLabel.CO_PAY,
            [EChargeType.OTHER]: EChargeTypeLabel.OTHER,
            [EChargeType.TEXT_VISIT]: EChargeTypeLabel.TEXT_VISIT,
        };
        if (textMap[item.chargeType as EChargeType]) {
            ret += ` ${textMap[item.chargeType as EChargeType]}`;
        }
        if (item.chargeToName) {
            ret += ` · Charged by: ${item.chargeToName}`;
        }
        const amountText = item.amountCents ? `$${(item.amountCents / 100).toFixed(2)}` : '-';
        ret += ` · ${amountText}`;

        return ret;
    };

    useEffect(() => {
        const fetchFileUrl = async () => {
            if (inboxMessage?.file?.fileUri) {
                setIsLoadingFile(true);
                try {
                    const fn = type === 'OPS' ? getPrivateFilePreview : getProviderPrivateFileSigned;
                    const res = await fn(inboxMessage.file.fileUri);
                    if (!res.error && res.data?.data?.singedUrl) {
                        setFileUrl(res.data.data.singedUrl);
                    }
                } catch (error) {
                    console.error(error);
                } finally {
                    setIsLoadingFile(false);
                }
            }
        };
        fetchFileUrl();
    }, [inboxMessage?.file?.fileUri]);

    // Calculate alignment and showName for TEXT_VISIT messages
    // TEXT_VISIT messages always have senderType === "SYSTEM"
    // We need to determine alignment based on textVisit.type and latestMessage
    const textVisitRightToLeft = (() => {
        if (!inboxMessage?.textVisit) return false;
        const textVisitType = inboxMessage.textVisit.type;
        const { latestMessage } = inboxMessage.textVisit;

        // TEXT_VISIT_TREATMENT_PLAN_SUBMITTED: Provider submitted visit notes -> show on left (provider's action)
        if (textVisitType === ETextVisitType.TEXT_VISIT_TREATMENT_PLAN_SUBMITTED) {
            return true;
        }

        // NEW_FOLLOW_UP_MESSAGE_CREATED: Check latestMessage.senderType
        if (
            textVisitType === ETextVisitType.NEW_FOLLOW_UP_MESSAGE_CREATED &&
            latestMessage?.senderType === ETextVisitSenderType.PROVIDER
        ) {
            return true;
        }

        return false;
    })();

    const videoVisitRightToLeft = (() => {
        if (inboxMessage?.appointment?.type === EAppointmentType.TREATMENT_PLAN_SUBMITTED) {
            return true;
        }
        if (
            inboxMessage?.messageType === EMessageType.TEXT_VISIT &&
            inboxMessage?.textVisit?.type === ETextVisitType.NEW_TEXT_VISIT_PROVIDER_CREATED
        ) {
            return true;
        }
        return false;
    })();

    const appointment = inboxMessage?.appointment;
    let appointmentTitle = '';
    let appointmentTips = 'Please review the details and get ready to meet your new patient!';
    let appointmentDateLabel = '';
    let appointmentDurationLabel = '';
    let oldTimeVal = '';
    let newTimeVal = '';
    let feeLabel = '';
    if (appointment) {
        if (appointment.type === EAppointmentType.NEW_APPOINTMENT_CREATED) {
            appointmentTitle = '🎊 Congratulations! You have a new appointment:';
        }
        if (appointment.type === EAppointmentType.RESCHEDULED) {
            appointmentTitle = '🔄 Appointment rescheduled:';
            appointmentTips =
                'You may want to reach out to the client to reschedule or assist with rebooking at a more convenient time.';
            feeLabel = 'Reschedule fee:';
            //Tuesday, March 3, 2025, 8:30 - 9:00 am
            if (timezone) {
                oldTimeVal = `${dayjs(appointment.oldStartAt).tz(timezone).format('dddd, MMMM D, YYYY, hh:mm A')} - ${dayjs(appointment.oldStartAt).tz(timezone).add(appointment.duration, 'minutes').format('hh:mm A')}`;
                newTimeVal = `${dayjs(appointment.startAt).tz(timezone).format('dddd, MMMM D, YYYY, hh:mm A')} - ${dayjs(appointment.startAt).tz(timezone).add(appointment.duration, 'minutes').format('hh:mm A')}`;
            } else {
                oldTimeVal = `${dayjs(appointment.oldStartAt).format('dddd, MMMM D, YYYY, hh:mm A')} - ${dayjs(appointment.oldStartAt).add(appointment.duration, 'minutes').format('hh:mm A')}`;
                newTimeVal = `${dayjs(appointment.startAt).format('dddd, MMMM D, YYYY, hh:mm A')} - ${dayjs(appointment.startAt).add(appointment.duration, 'minutes').format('hh:mm A')}`;
            }
        }
        if (appointment.type === EAppointmentType.PATIENT_CANCELED) {
            appointmentTitle = '❌ Appointment canceled:';
            appointmentTips =
                'You may want to reach out to the client to reschedule or assist with rebooking at a more convenient time.';
        }
        if (appointment.type === EAppointmentType.PROVIDER_CANCELED) {
            appointmentTitle = '🎉 You have successfully cancelled the patient appointment:';
            appointmentTips = '';
        }
        if (appointment.type === EAppointmentType.PATIENT_LATE_CANCELED) {
            appointmentTitle = '❌ Appointment late canceled:';
            appointmentTips =
                'You may want to reach out to the client to reschedule or assist with rebooking at a more convenient time.';
            feeLabel = 'Late cancellation fee:';
        }

        if (timezone) {
            appointmentDateLabel = dayjs(appointment.startAt).tz(timezone).format('dddd, MMMM D, YYYY, hh:mm A');
            appointmentDurationLabel = `${dayjs(appointment.startAt)
                .tz(timezone)
                .format(
                    dayjs(appointment.startAt)?.hour() === 0 ? 'HH:mm' : 'hh:mm',
                )} - ${dayjs(appointment.startAt).tz(timezone).add(appointment.duration, 'minutes').format('hh:mm A')} ${timezone} | ${appointment.duration} mins`;
        } else {
            appointmentDateLabel = dayjs(appointment.startAt).format('dddd, MMMM D, YYYY');
            appointmentDurationLabel = `${dayjs(appointment.startAt).format(dayjs(appointment.startAt)?.hour() === 0 ? 'HH:mm' : 'hh:mm')} - ${dayjs(appointment.startAt).add(appointment.duration, 'minutes').format('hh:mm A')} | ${appointment.duration} mins`;
        }
    }

    const handleClickViewAppointment = async (id: number) => {
        if (!id) {
            return;
        }
        setLoading(true);
        const res = await getCalendarAppointmentEventDetail(`${id}`);
        setLoading(false);
        if (!res.error) {
            const resData: TServerCalendarAppointmentEventDetail | undefined = res?.data?.data || undefined;
            if (resData) {
                window.open(
                    `${PATH.DASHBOARD}/${PATH.CALENDAR}?aid=${resData?.id}&date=${getTimeStr(dayjs(resData?.startAt), 'MM-DD-YYYY')}`,
                    '_blank',
                );
            }
        }
    };

    const getHtmlContent = (content?: string, fromProvider?: boolean) => {
        if (!content) return '';
        let ret = content.replace(/\n/g, '<br />');
        if (fromProvider) {
            const linkRegex = /(https?:\/\/[^\s"'<>]+[^\s"'<>.,;!?)]*)/g;
            ret = ret.replace(linkRegex, (match) => `<a href="${match}" target="_blank">${match}</a>`);
        }
        return sanitizeRichHtml(ret);
    };

    const handleViewVideoVisit = () => {
        if (appointment?.id) {
            onViewVideoVisit?.(appointment.id);
        }
    };

    const handleViewTextVisit = () => {
        if (inboxMessage?.textVisit?.textVisitUuid) {
            onViewTextVisit?.(inboxMessage.textVisit.textVisitUuid);
        }
    };

    const handleAcceptTextVisit = (openChargeAfterSubmit?: boolean) => {
        if (inboxMessage?.textVisit?.textVisitUuid) {
            onAcceptTextVisit?.(inboxMessage.textVisit.textVisitUuid, openChargeAfterSubmit);
        }
    };

    const getTextVisitConditionText = () => {
        if (!inboxMessage?.textVisit || !inboxMessage?.textVisit?.serviceType) return '';
        // 优先显示 assessmentName
        if (inboxMessage?.textVisit?.assessmentName) {
            return inboxMessage.textVisit.assessmentName;
        }
        // Fallback 到原来的逻辑：显示 condition
        return inboxMessage?.textVisit?.serviceType === TextVisitServiceType.INITIAL_VISIT
            ? inboxMessage?.textVisit?.conditions
            : ETextVisitConditionMap[inboxMessage.textVisit.serviceType];
    };

    const finalRightToLeft =
        inboxMessage?.textVisit ||
        inboxMessage?.appointment?.type === EAppointmentType.TREATMENT_PLAN_SUBMITTED ||
        inboxMessage?.messageType === EMessageType.NEW_TEXT_VISIT_PROVIDER_CREATED
            ? textVisitRightToLeft || videoVisitRightToLeft
            : rightToLeft;

    return (
        <div className={`${s.wrap} ${finalRightToLeft ? s.wrapRight : ''}`}>
            {inboxMessage?.text && (
                <div className={s.textItem}>
                    {inboxMessage.createdAt && inboxMessage.showSendTime && (
                        <div className={s.sendTimeWrap}>
                            {showName ? `${firstName} ${lastName}`.trim() : ''}
                            <span className={s.sendTimeVal}>
                                {getTimeStr(dayjs(inboxMessage.createdAt), 'hh:mm A')}
                            </span>
                        </div>
                    )}
                    <div
                        className={s.textWrap}
                        dangerouslySetInnerHTML={{
                            __html: getHtmlContent(
                                inboxMessage.text.body,
                                inboxMessage.senderType === EMessageSenderType.PROVIDER ||
                                    inboxMessage.senderType === EMessageSenderType.OPS,
                            ),
                        }}
                    />
                </div>
            )}

            {appointment &&
                ![
                    EAppointmentType.VISIT_REASSIGNED_PATIENT,
                    EAppointmentType.VISIT_REASSIGNED_NEW_PROVIDER,
                    EAppointmentType.VISIT_REASSIGNED_OLD_PROVIDER,
                ].includes(appointment.type) &&
                (appointment.type === EAppointmentType.TREATMENT_PLAN_SUBMITTED ? (
                    <div className={s.textItem}>
                        <div className={s.sendTimeWrap}>
                            {showName ? `${firstName} ${lastName}`.trim() : ''}
                            <span className={s.sendTimeVal}>
                                {getTimeStr(dayjs(inboxMessage.createdAt), 'hh:mm A')}
                            </span>
                        </div>
                        <div className={s.textVisitTreatmentWrap}>
                            <div>
                                <svg
                                    xmlns="http://www.w3.org/2000/svg"
                                    width="30"
                                    height="31"
                                    viewBox="0 0 30 31"
                                    fill="none"
                                >
                                    <rect y="0.424316" width="30" height="30" rx="10" fill="#0E62F4" />
                                    <path
                                        d="M12.3214 15.4249H17.6786M12.3214 18.9963H17.6786M19.4643 23.4606H10.5357C9.54949 23.4606 8.75 22.6611 8.75 21.6749V9.17487C8.75 8.18865 9.54949 7.38916 10.5357 7.38916H15.523C15.7598 7.38916 15.9869 7.48323 16.1544 7.65067L20.9885 12.4848C21.1559 12.6522 21.25 12.8793 21.25 13.1161V21.6749C21.25 22.6611 20.4505 23.4606 19.4643 23.4606Z"
                                        stroke="#F8F4FE"
                                        strokeWidth="1.42857"
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                    />
                                </svg>
                            </div>
                            <div className="flex flex-col gap-3 items-start">
                                <div>You’ve sent a visit notes to patient regarding their video visit.</div>
                                <Button
                                    style={{ background: '#0E62F4', color: '#fff', border: 'none' }}
                                    onClick={handleViewVideoVisit}
                                >
                                    View visit notes
                                </Button>
                            </div>
                        </div>
                    </div>
                ) : (
                    <div className={s.appItem}>
                        <div className={s.sendTimeWrap}>
                            {showName ? `${firstName} ${lastName}`.trim() : ''}
                            <span className={s.sendTimeVal}>
                                {getTimeStr(dayjs(inboxMessage.createdAt), 'hh:mm A')}
                            </span>
                        </div>
                        <Spin spinning={loading} className={s.appSpin} wrapperClassName={s.appSpinWrap}>
                            <div className={s.appWrap}>
                                <div className={s.appTitle}>{appointmentTitle}</div>
                                <ul className={s.appInfoList}>
                                    <li className={s.appName}>
                                        {appointment.serviceState ? `[${appointment.serviceState}] ` : ''}{' '}
                                        {appointment?.appointmentName}
                                    </li>
                                    <li className={s.appClient}>
                                        <div className={s.label}>Client:</div>
                                        <div
                                            onClick={() => {
                                                if (appointment.clientId) {
                                                    window.open(
                                                        `${PATH.DASHBOARD}/${PATH.PROSPECTS_DETAIL}/${appointment?.clientId}`,
                                                        '_blank',
                                                    );
                                                }
                                            }}
                                            className={s.appLink}
                                        >
                                            {`${inboxConversationInfo?.patientFirstName} ${inboxConversationInfo?.patientLastName}`}
                                        </div>
                                    </li>
                                    {appointment.type !== EAppointmentType.RESCHEDULED && (
                                        <>
                                            <li className={s.appInfoItem}>
                                                <div className={s.label}>Date:</div>
                                                <div onClick={() => {}} className={s.value}>
                                                    {/* Tuesday, March 3, 2025 */}
                                                    {appointmentDateLabel}
                                                </div>
                                            </li>
                                            <li className={s.appInfoItem}>
                                                <div className={s.label}>Time:</div>
                                                <div onClick={() => {}} className={s.value}>
                                                    {appointmentDurationLabel}
                                                </div>
                                            </li>
                                        </>
                                    )}
                                    {appointment.type === EAppointmentType.RESCHEDULED && (
                                        <>
                                            <li className={s.appInfoItem}>
                                                <div className={s.label}>Original Time:</div>
                                                <div style={{ textDecoration: 'line-through' }} className={s.value}>
                                                    {/* Tuesday, March 3, 2025 */}
                                                    {oldTimeVal}
                                                </div>
                                            </li>
                                            <li className={s.appInfoItem}>
                                                <div className={s.label}>New Time:</div>
                                                <div className={s.value}>{newTimeVal}</div>
                                            </li>
                                        </>
                                    )}
                                    {[EAppointmentType.RESCHEDULED, EAppointmentType.PATIENT_LATE_CANCELED].includes(
                                        appointment.type,
                                    ) && (
                                        <li className={s.appInfoItem}>
                                            <div className={s.label}>{feeLabel}</div>
                                            <div className={s.value}>
                                                {appointment.chargeFee
                                                    ? `$${appointment.chargeFee}`
                                                    : 'Not applied based on your billing policy'}
                                            </div>
                                        </li>
                                    )}
                                </ul>
                                {appointment.type !== EAppointmentType.RESCHEDULED && (
                                    <div className={s.appTips}>{appointmentTips}</div>
                                )}

                                <div
                                    onClick={() => {
                                        if (appointment.id) {
                                            handleClickViewAppointment(appointment.id);
                                        }
                                    }}
                                    className={s.appLink}
                                >
                                    View appointment
                                </div>
                            </div>
                        </Spin>
                    </div>
                ))}
            {appointment &&
                [
                    EAppointmentType.VISIT_REASSIGNED_OLD_PROVIDER,
                    EAppointmentType.VISIT_REASSIGNED_NEW_PROVIDER,
                    EAppointmentType.VISIT_REASSIGNED_PATIENT,
                ].includes(appointment.type) && (
                    <div className={s.reassignItem}>
                        <div className={s.sendTimeWrap}>
                            {showName ? `${firstName} ${lastName}`.trim() : ''}
                            <span className={s.sendTimeVal}>
                                {getTimeStr(dayjs(inboxMessage.createdAt), 'hh:mm A')}
                            </span>
                        </div>
                        <div className={s.reassignCard}>
                            <div className={s.reassignCardHdr}>
                                <svg
                                    fill="none"
                                    viewBox="0 0 24 24"
                                    stroke="currentColor"
                                    strokeWidth={2}
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                >
                                    <polyline points="17 1 21 5 17 9" />
                                    <path d="M3 11V9a4 4 0 014-4h14" />
                                    <polyline points="7 23 3 19 7 15" />
                                    <path d="M21 13v2a4 4 0 01-4 4H3" />
                                </svg>
                                <span className={s.reassignCardTitle}>Visit reassigned</span>
                            </div>
                            <div className={s.reassignCardRows}>
                                {appointment.type === EAppointmentType.VISIT_REASSIGNED_OLD_PROVIDER && (
                                    <>
                                        <div className={s.reassignIntro}>
                                            {`${appointment.patientName || inboxConversationInfo?.patientFirstName || ''}'s visit has been reassigned from your schedule to ${appointment.newProviderName || ''}.`}
                                        </div>
                                        <div className={s.reassignDetailRow}>
                                            <span className={s.reassignDetailLabel}>Patient</span>
                                            <span className={s.reassignDetailVal}>
                                                <strong>
                                                    {appointment.patientName ||
                                                        `${inboxConversationInfo?.patientFirstName || ''} ${inboxConversationInfo?.patientLastName || ''}`.trim()}
                                                </strong>
                                            </span>
                                        </div>
                                        <div className={s.reassignDetailRow}>
                                            <span className={s.reassignDetailLabel}>Visit</span>
                                            <span className={s.reassignDetailVal}>{appointment.appointmentName}</span>
                                        </div>
                                        <div className={s.reassignDetailRow}>
                                            <span className={s.reassignDetailLabel}>Date</span>
                                            <span className={s.reassignDetailVal}>
                                                {appointmentDateLabel}
                                                {appointmentDurationLabel ? ` · ${appointmentDurationLabel}` : ''}
                                            </span>
                                        </div>
                                        <div className={s.reassignDetailRow}>
                                            <span className={s.reassignDetailLabel}>New provider</span>
                                            <span className={s.reassignDetailVal}>
                                                <strong>{appointment.newProviderName || ''}</strong>
                                            </span>
                                        </div>
                                        <div className={s.reassignInfoBox}>
                                            <svg
                                                fill="none"
                                                viewBox="0 0 24 24"
                                                stroke="currentColor"
                                                strokeWidth={2}
                                                strokeLinecap="round"
                                                strokeLinejoin="round"
                                            >
                                                <circle cx="12" cy="12" r="10" />
                                                <line x1="12" y1="8" x2="12" y2="12" />
                                                <line x1="12" y1="16" x2="12.01" y2="16" />
                                            </svg>
                                            <span>{`The intake forms have been transferred to ${appointment.newProviderName || ''}. No action required on your end.`}</span>
                                        </div>
                                    </>
                                )}
                                {appointment.type === EAppointmentType.VISIT_REASSIGNED_NEW_PROVIDER && (
                                    <>
                                        <div className={s.reassignIntro}>
                                            A new visit has been assigned to you. Please review the patient details
                                            below.
                                        </div>
                                        <div className={s.reassignDetailRow}>
                                            <span className={s.reassignDetailLabel}>Patient</span>
                                            <span className={s.reassignDetailVal}>
                                                {appointment.clientId ? (
                                                    <a
                                                        href={`${PATH.DASHBOARD}/${PATH.PROSPECTS_DETAIL}/${appointment.clientId}`}
                                                        target="_blank"
                                                        rel="noreferrer"
                                                        style={{ fontWeight: 600 }}
                                                    >
                                                        {appointment.patientName ||
                                                            `${inboxConversationInfo?.patientFirstName || ''} ${inboxConversationInfo?.patientLastName || ''}`.trim()}
                                                    </a>
                                                ) : (
                                                    <strong>
                                                        {appointment.patientName ||
                                                            `${inboxConversationInfo?.patientFirstName || ''} ${inboxConversationInfo?.patientLastName || ''}`.trim()}
                                                    </strong>
                                                )}
                                            </span>
                                        </div>
                                        <div className={s.reassignDetailRow}>
                                            <span className={s.reassignDetailLabel}>Visit</span>
                                            <span className={s.reassignDetailVal}>{appointment.appointmentName}</span>
                                        </div>
                                        <div className={s.reassignDetailRow}>
                                            <span className={s.reassignDetailLabel}>Date</span>
                                            <span className={s.reassignDetailVal}>
                                                {appointmentDateLabel}
                                                {appointmentDurationLabel ? ` · ${appointmentDurationLabel}` : ''}
                                            </span>
                                        </div>
                                        <div className={s.reassignDetailRow}>
                                            <span className={s.reassignDetailLabel}>Reassigned</span>
                                            <span className={s.reassignDetailVal}>
                                                {appointment.oldProviderName || ''} →{' '}
                                                <strong>{appointment.newProviderName || ''}</strong>
                                            </span>
                                        </div>
                                        {appointment.meetingLink && (
                                            <div className={s.reassignDetailRow}>
                                                <span className={s.reassignDetailLabel}>Meeting link</span>
                                                <span className={`${s.reassignDetailVal} ${s.reassignDetailValCol}`}>
                                                    <span>Join with Klarity Meet:</span>
                                                    <a href={appointment.meetingLink} target="_blank" rel="noreferrer">
                                                        {appointment.meetingLink}
                                                    </a>
                                                </span>
                                            </div>
                                        )}
                                        {appointment.intakeFormsLink && (
                                            <div className={s.reassignDetailRow}>
                                                <span className={s.reassignDetailLabel}>Intake forms</span>
                                                <span className={s.reassignDetailVal}>
                                                    <a
                                                        href={appointment.intakeFormsLink}
                                                        target="_blank"
                                                        rel="noreferrer"
                                                    >
                                                        View intake forms
                                                    </a>
                                                </span>
                                            </div>
                                        )}
                                        {appointment.visitNotesLink && (
                                            <div className={s.reassignDetailRow}>
                                                <span className={s.reassignDetailLabel}>Visit notes</span>
                                                <span className={s.reassignDetailVal}>
                                                    <a
                                                        href={appointment.visitNotesLink}
                                                        target="_blank"
                                                        rel="noreferrer"
                                                    >
                                                        Add visit notes
                                                    </a>
                                                </span>
                                            </div>
                                        )}
                                    </>
                                )}
                                {appointment.type === EAppointmentType.VISIT_REASSIGNED_PATIENT && (
                                    <>
                                        <div className={s.reassignIntro}>
                                            Your visit has been reassigned to a new provider. Please see details below.
                                        </div>
                                        <div className={s.reassignDetailRow}>
                                            <span className={s.reassignDetailLabel}>Visit</span>
                                            <span className={s.reassignDetailVal}>{appointment.appointmentName}</span>
                                        </div>
                                        <div className={s.reassignDetailRow}>
                                            <span className={s.reassignDetailLabel}>Date</span>
                                            <span className={s.reassignDetailVal}>
                                                {appointmentDateLabel}
                                                {appointmentDurationLabel ? ` · ${appointmentDurationLabel}` : ''}
                                            </span>
                                        </div>
                                        <div className={s.reassignDetailRow}>
                                            <span className={s.reassignDetailLabel}>New provider</span>
                                            <span className={s.reassignDetailVal}>
                                                <strong>{appointment.newProviderName || ''}</strong>
                                            </span>
                                        </div>
                                        {appointment.meetingLink && (
                                            <div className={s.reassignDetailRow}>
                                                <span className={s.reassignDetailLabel}>Meeting link</span>
                                                <span className={`${s.reassignDetailVal} ${s.reassignDetailValCol}`}>
                                                    <span>Join with Klarity Meet:</span>
                                                    <a href={appointment.meetingLink} target="_blank" rel="noreferrer">
                                                        {appointment.meetingLink}
                                                    </a>
                                                </span>
                                            </div>
                                        )}
                                        {appointment.intakeFormsLink && (
                                            <div className={s.reassignDetailRow}>
                                                <span className={s.reassignDetailLabel}>Intake forms</span>
                                                <span className={s.reassignDetailVal}>
                                                    <a
                                                        href={appointment.intakeFormsLink}
                                                        target="_blank"
                                                        rel="noreferrer"
                                                    >
                                                        View intake forms
                                                    </a>
                                                </span>
                                            </div>
                                        )}
                                    </>
                                )}
                            </div>
                        </div>
                    </div>
                )}
            {inboxMessage?.timeLabel && <div className={s.dateWrap}>{inboxMessage.timeLabel}</div>}
            {inboxMessage?.question && (
                <div className={s.qsItem}>
                    <div className={s.sendTimeWrap}>
                        {showName ? `${firstName} ${lastName}`.trim() : ''}
                        <span className={s.sendTimeVal}>{getTimeStr(dayjs(inboxMessage.createdAt), 'hh:mm A')}</span>
                    </div>
                    <div className={s.qsWrap}>
                        <div className={s.subject}>{inboxMessage.question.subject}</div>
                        <div
                            className={s.content}
                            dangerouslySetInnerHTML={{
                                __html: getHtmlContent(
                                    inboxMessage.question.body,
                                    inboxMessage.senderType === EMessageSenderType.PROVIDER,
                                ),
                            }}
                        />
                    </div>
                </div>
            )}
            {inboxMessage?.avaReply && (
                <div className={s.avaItem}>
                    <div className={s.sendTimeWrap}>
                        {/* {
                                showName ? `${firstName} ${lastName}`.trim() : ''
                            } */}
                        <span className={s.sendTimeVal}>{getTimeStr(dayjs(inboxMessage.createdAt), 'hh:mm A')}</span>
                    </div>
                    <div className={s.avaWrap}>
                        <div
                            className={s.avaContent}
                            dangerouslySetInnerHTML={{
                                __html: getHtmlContent(
                                    inboxMessage.avaReply.body,
                                    inboxMessage.senderType === EMessageSenderType.PROVIDER,
                                ),
                            }}
                        />
                        <div className={s.avaIcon}>
                            <svg
                                xmlns="http://www.w3.org/2000/svg"
                                width="13"
                                height="13"
                                viewBox="0 0 13 13"
                                fill="none"
                            >
                                <path
                                    fillRule="evenodd"
                                    clipRule="evenodd"
                                    d="M6.3019 11.292C8.92776 11.292 11.0564 9.16336 11.0564 6.5375C11.0564 3.91164 8.92776 1.78296 6.3019 1.78296C3.67604 1.78296 1.54736 3.91164 1.54736 6.5375C1.54736 9.16336 3.67604 11.292 6.3019 11.292ZM8.5051 5.76911C8.73719 5.53701 8.73719 5.16071 8.5051 4.92862C8.273 4.69652 7.8967 4.69652 7.66461 4.92862L5.70758 6.88564L4.9392 6.11725C4.7071 5.88516 4.3308 5.88516 4.0987 6.11725C3.86661 6.34935 3.86661 6.72565 4.0987 6.95774L5.28734 8.14638C5.51943 8.37847 5.89573 8.37847 6.12783 8.14638L8.5051 5.76911Z"
                                    fill="#069D70"
                                />
                            </svg>
                            Replied by
                            <img width={26} src={AvaIcon} />
                        </div>
                    </div>
                </div>
            )}
            {inboxMessage?.messageType === EMessageType.UNREAD_MARK && (
                <div className={s.unreadWrap}>
                    <span className={s.unreadText}>Unread messages</span>
                </div>
            )}
            {inboxMessage?.messageType === EMessageType.TEXT_VISIT &&
                inboxMessage?.textVisit?.type === ETextVisitType.NEW_TEXT_VISIT_CREATED && (
                    <div className={s.textItem}>
                        <div className={s.sendTimeWrap}>
                            {showName ? `${firstName} ${lastName}`.trim() : ''}
                            <span className={s.sendTimeVal}>
                                {getTimeStr(dayjs(inboxMessage.createdAt), 'hh:mm A')}
                            </span>
                        </div>
                        <div className={s.textVisitWrap}>
                            <div>
                                <svg
                                    xmlns="http://www.w3.org/2000/svg"
                                    width="30"
                                    height="31"
                                    viewBox="0 0 30 31"
                                    fill="none"
                                >
                                    <rect y="0.424316" width="30" height="30" rx="10" fill="#B300FF" />
                                    <path
                                        d="M12.3214 15.4249H17.6786M12.3214 18.9963H17.6786M19.4643 23.4606H10.5357C9.54949 23.4606 8.75 22.6611 8.75 21.6749V9.17487C8.75 8.18865 9.54949 7.38916 10.5357 7.38916H15.523C15.7598 7.38916 15.9869 7.48323 16.1544 7.65067L20.9885 12.4848C21.1559 12.6522 21.25 12.8793 21.25 13.1161V21.6749C21.25 22.6611 20.4505 23.4606 19.4643 23.4606Z"
                                        stroke="#F8F4FE"
                                        strokeWidth="1.42857"
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                    />
                                </svg>
                            </div>
                            <div className="flex flex-col gap-3 items-start">
                                <div>
                                    You have a new text visit request for {getTextVisitConditionText()}. Please review
                                    and respond as soon as possible.
                                </div>
                                <Button
                                    style={{ background: '#000', color: '#fff' }}
                                    onClick={() => {
                                        const _openChargeAfterSubmit = !!getTextVisitConditionText()
                                            ?.toLowerCase()
                                            ?.includes('medication refill');
                                        handleAcceptTextVisit(_openChargeAfterSubmit);
                                    }}
                                >
                                    View request
                                </Button>
                            </div>
                        </div>
                    </div>
                )}
            {inboxMessage?.messageType === EMessageType.TEXT_VISIT &&
                inboxMessage?.textVisit?.type === ETextVisitType.TEXT_VISIT_TREATMENT_PLAN_SUBMITTED && (
                    <div className={s.textItem}>
                        <div className={s.sendTimeWrap}>
                            {showName ? `${firstName} ${lastName}`.trim() : ''}
                            <span className={s.sendTimeVal}>
                                {getTimeStr(dayjs(inboxMessage.createdAt), 'hh:mm A')}
                            </span>
                        </div>
                        <div className={s.textVisitTreatmentWrap}>
                            <div>
                                <svg
                                    xmlns="http://www.w3.org/2000/svg"
                                    width="30"
                                    height="31"
                                    viewBox="0 0 30 31"
                                    fill="none"
                                >
                                    <rect y="0.424316" width="30" height="30" rx="10" fill="#B300FF" />
                                    <path
                                        d="M12.3214 15.4249H17.6786M12.3214 18.9963H17.6786M19.4643 23.4606H10.5357C9.54949 23.4606 8.75 22.6611 8.75 21.6749V9.17487C8.75 8.18865 9.54949 7.38916 10.5357 7.38916H15.523C15.7598 7.38916 15.9869 7.48323 16.1544 7.65067L20.9885 12.4848C21.1559 12.6522 21.25 12.8793 21.25 13.1161V21.6749C21.25 22.6611 20.4505 23.4606 19.4643 23.4606Z"
                                        stroke="#F8F4FE"
                                        strokeWidth="1.42857"
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                    />
                                </svg>
                            </div>
                            <div className="flex flex-col gap-3 items-start">
                                <div>
                                    You’ve sent a visit notes to patient regarding their text visit for{' '}
                                    {getTextVisitConditionText()}.
                                </div>
                                <Button
                                    style={{ background: '#B200FF', color: '#fff', border: 'none' }}
                                    onClick={handleViewTextVisit}
                                >
                                    View visit notes
                                </Button>
                            </div>
                        </div>
                    </div>
                )}
            {inboxMessage?.messageType === EMessageType.TEXT_VISIT &&
                inboxMessage?.textVisit?.type === ETextVisitType.NEW_FOLLOW_UP_MESSAGE_CREATED && (
                    <div className={s.textItem}>
                        <div className={s.sendTimeWrap}>
                            {showName ? `${firstName} ${lastName}`.trim() : ''}
                            <span className={s.sendTimeVal}>
                                {getTimeStr(dayjs(inboxMessage.createdAt), 'hh:mm A')}
                            </span>
                        </div>
                        <div className={s.textWrap}>
                            <div className="text-sm font-bold mb-4">
                                {inboxMessage.textVisit.latestMessage?.senderType === ETextVisitSenderType.PROVIDER
                                    ? `You’ve sent a response to the patient regarding their text visit for ${getTextVisitConditionText()}:`
                                    : `You’ve received a follow-up question from the patient regarding their text visit for ${getTextVisitConditionText()}:`}
                            </div>
                            <div
                                className="text-sm mb-2"
                                dangerouslySetInnerHTML={{
                                    __html: sanitizeRichHtml((inboxMessage.textVisit.latestMessage?.message || '').replace(/\n/g, '<br />')),
                                }}
                            />
                            <Button style={{ background: '#000', color: '#fff' }} onClick={handleViewTextVisit}>
                                {inboxMessage.textVisit.latestMessage?.senderType === ETextVisitSenderType.PROVIDER
                                    ? 'View details'
                                    : 'Reply now'}
                            </Button>
                        </div>
                    </div>
                )}
            {inboxMessage?.messageType === EMessageType.TEXT_VISIT &&
                inboxMessage?.textVisit?.type === ETextVisitType.NEW_TEXT_VISIT_PROVIDER_CREATED && (
                    <div className={s.textItem}>
                        <div className={s.sendTimeWrap}>
                            {showName ? `${firstName} ${lastName}`.trim() : ''}
                            <span className={s.sendTimeVal}>
                                {getTimeStr(dayjs(inboxMessage.createdAt), 'hh:mm A')}
                            </span>
                        </div>
                        <div className={s.textVisitRefillWrap}>
                            <div>
                                <svg
                                    xmlns="http://www.w3.org/2000/svg"
                                    width="30"
                                    height="30"
                                    viewBox="0 0 30 30"
                                    fill="none"
                                >
                                    <rect width="30" height="30" rx="10" fill="#B300FF" />
                                    <path
                                        d="M12.3214 15.0006H17.6786M12.3214 18.572H17.6786M19.4643 23.0363H10.5357C9.54949 23.0363 8.75 22.2368 8.75 21.2506V8.75056C8.75 7.76434 9.54949 6.96484 10.5357 6.96484H15.523C15.7598 6.96484 15.9869 7.05891 16.1544 7.22636L20.9885 12.0605C21.1559 12.2279 21.25 12.455 21.25 12.6918V21.2506C21.25 22.2368 20.4505 23.0363 19.4643 23.0363Z"
                                        stroke="#F8F4FE"
                                        strokeWidth="1.42857"
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                    />
                                </svg>
                            </div>
                            <div className="flex flex-col gap-3 items-start">
                                <div>
                                    <p>You scheduled a prescription refill text visit for this patient.</p>
                                    <p>Medication: {medicationName}</p>
                                </div>
                                {!blockOpenTextVisitModal && (
                                    <Button
                                        style={{ background: '#2E2E2E', color: '#fff', border: 'none' }}
                                        onClick={() => {
                                            handleAcceptTextVisit(true);
                                        }}
                                    >
                                        Start refill
                                    </Button>
                                )}
                            </div>
                        </div>
                    </div>
                )}
            {inboxMessage?.email && (
                <div className={s.emailItem}>
                    <div className={s.sendTimeWrap}>
                        {showName ? `${firstName} ${lastName}`.trim() : ''}
                        <span className={s.sendTimeVal}>{getTimeStr(dayjs(inboxMessage.createdAt), 'hh:mm A')}</span>
                    </div>
                    <div className={s.emailWrap}>
                        <div className={s.subject}>{inboxMessage.email.subject}</div>
                        <div
                            className={s.content}
                            dangerouslySetInnerHTML={{
                                __html: getHtmlContent(
                                    inboxMessage.email.body,
                                    inboxMessage.senderType === EMessageSenderType.PROVIDER,
                                ),
                            }}
                        />
                    </div>
                </div>
            )}
            {inboxMessage?.review && (
                <ReviewMessage
                    review={inboxMessage.review}
                    createdAt={inboxMessage.createdAt}
                    showSendTime={inboxMessage.showSendTime}
                    onReplySuccess={onReviewReplySuccess}
                />
            )}
            {inboxMessage?.messageType === EMessageType.KLARITY_SUPPORT && inboxMessage?.supportMessage && (
                <div className={s.supportItem}>
                    <div className={s.sendTimeWrap}>
                        {showName ? `${firstName} ${lastName}`.trim() : ''}
                        <span className={s.sendTimeVal}>{getTimeStr(dayjs(inboxMessage.createdAt), 'hh:mm A')}</span>
                    </div>
                    <div className={s.supportWrap}>
                        <div className={s.supportTitle}>
                            {inboxMessage.supportMessage.category === 'BILLING_AND_PAYMENTS'
                                ? 'Billing and Payments'
                                : 'Other questions '}
                        </div>
                        {/* Refund request / Question about a charge / Other billing questions */}
                        {inboxMessage.supportMessage.subCategory &&
                            inboxMessage.supportMessage.subCategory === ESubCategory.REFUND_REQUEST && (
                                <div className={s.supportSubTitle}>Type: Refund request</div>
                            )}
                        {inboxMessage.supportMessage.subCategory &&
                            inboxMessage.supportMessage.subCategory === ESubCategory.CHARGE_REQUEST && (
                                <div className={s.supportSubTitle}>Type: Question about a charge</div>
                            )}
                        {inboxMessage.supportMessage.subCategory &&
                            inboxMessage.supportMessage.subCategory === ESubCategory.OTHER_BILLING_QUESTIONS && (
                                <div className={s.supportSubTitle}>Type: Other billing questions (required)</div>
                            )}
                        {inboxMessage.supportMessage.chargeToType && (
                            <div className={s.supportSubTitle}>{getChargeDetailText(inboxMessage.supportMessage)}</div>
                        )}
                        {inboxMessage.supportMessage.note && (
                            <div className={s.supportSubTitle}>Details: {inboxMessage.supportMessage.note}</div>
                        )}
                    </div>
                </div>
            )}
            {inboxMessage?.transferMessage && (
                <div className={s.transferItem}>
                    {inboxMessage.transferMessage.body.currentOpsName} transferred this conversation to{' '}
                    {inboxMessage.transferMessage.body.newOpsName}.
                </div>
            )}
            {inboxMessage?.closeMessage && (
                <div className={s.transferItem}>
                    Chat closed for the patient. {inboxMessage.closeMessage.body.patientName} can no longer view or
                    reply to this conversation.
                </div>
            )}
            {inboxMessage?.treatmentPlanMessage?.type === 'TREATMENT_PLAN_SUBMITTED' && (
                <div className={s.textItem}>
                    <div className={s.sendTimeWrap}>
                        {showName ? `${firstName} ${lastName}`.trim() : ''}
                        <span className={s.sendTimeVal}>{getTimeStr(dayjs(inboxMessage.createdAt), 'hh:mm A')}</span>
                    </div>
                    <div className={s.textVisitTreatmentWrap}>
                        <div>
                            <svg
                                xmlns="http://www.w3.org/2000/svg"
                                width="30"
                                height="31"
                                viewBox="0 0 30 31"
                                fill="none"
                            >
                                <rect y="0.424316" width="30" height="30" rx="10" fill="#0E62F4" />
                                <path
                                    d="M12.3214 15.4249H17.6786M12.3214 18.9963H17.6786M19.4643 23.4606H10.5357C9.54949 23.4606 8.75 22.6611 8.75 21.6749V9.17487C8.75 8.18865 9.54949 7.38916 10.5357 7.38916H15.523C15.7598 7.38916 15.9869 7.48323 16.1544 7.65067L20.9885 12.4848C21.1559 12.6522 21.25 12.8793 21.25 13.1161V21.6749C21.25 22.6611 20.4505 23.4606 19.4643 23.4606Z"
                                    stroke="#F8F4FE"
                                    strokeWidth="1.42857"
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                />
                            </svg>
                        </div>
                        <div className="flex flex-col gap-3 items-start">
                            <div>Provider has sent a visit note to patient.</div>
                        </div>
                    </div>
                </div>
            )}
            {inboxMessage?.treatmentPlanMessage?.type === 'TREATMENT_PLAN_ADDENDUM_CREATED' && (
                <div className={s.textItem}>
                    <div className={s.sendTimeWrap}>
                        {showName ? `${firstName} ${lastName}`.trim() : ''}
                        <span className={s.sendTimeVal}>{getTimeStr(dayjs(inboxMessage.createdAt), 'hh:mm A')}</span>
                    </div>
                    <div className={s.textVisitTreatmentWrap}>
                        <div>
                            <svg
                                xmlns="http://www.w3.org/2000/svg"
                                width="30"
                                height="31"
                                viewBox="0 0 30 31"
                                fill="none"
                            >
                                <rect y="0.424316" width="30" height="30" rx="10" fill="#0E62F4" />
                                <path
                                    d="M12.3214 15.4249H17.6786M12.3214 18.9963H17.6786M19.4643 23.4606H10.5357C9.54949 23.4606 8.75 22.6611 8.75 21.6749V9.17487C8.75 8.18865 9.54949 7.38916 10.5357 7.38916H15.523C15.7598 7.38916 15.9869 7.48323 16.1544 7.65067L20.9885 12.4848C21.1559 12.6522 21.25 12.8793 21.25 13.1161V21.6749C21.25 22.6611 20.4505 23.4606 19.4643 23.4606Z"
                                    stroke="#F8F4FE"
                                    strokeWidth="1.42857"
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                />
                            </svg>
                        </div>
                        <div className="flex flex-col gap-3 items-start">
                            <div>An addendum has been added to the visit note.</div>
                        </div>
                    </div>
                </div>
            )}
            {inboxMessage?.messageType === EMessageType.FILE &&
                (inboxMessage.file?.fileType.startsWith('image/') ? (
                    <>
                        <div className={s.imageItem}>
                            <Image
                                key={inboxMessage.file?.fileId}
                                src={fileUrl}
                                alt="file"
                                width={100}
                                height={100}
                                placeholder={isLoadingFile ? <Spin /> : undefined}
                                preview={false}
                                onClick={() => setPreviewVisible(true)}
                                style={{ cursor: 'pointer' }}
                            />
                        </div>
                        <PreviewImageModal
                            open={previewVisible}
                            onClose={() => setPreviewVisible(false)}
                            imageUrl={fileUrl!}
                            fileId={inboxMessage.file?.fileId}
                            fileName={inboxMessage.file?.fileName}
                        />
                    </>
                ) : (
                    <div className={s.fileItem}>
                        <div
                            className={s.fileInfo}
                            onClick={() => {
                                window.open(fileUrl, '_blank');
                            }}
                        >
                            <svg
                                className={s.pdfIcon}
                                xmlns="http://www.w3.org/2000/svg"
                                width="16"
                                height="16"
                                viewBox="0 0 16 16"
                                fill="none"
                            >
                                <path
                                    d="M9.33333 1.33334H4.00001C3.6464 1.33334 3.30724 1.47382 3.05719 1.72387C2.80715 1.97392 2.66667 2.31305 2.66667 2.66668V13.3333C2.66667 13.687 2.80715 14.0261 3.05719 14.2762C3.30724 14.5262 3.6464 14.6667 4.00001 14.6667H12C12.3536 14.6667 12.6928 14.5262 12.9428 14.2762C13.1929 14.0261 13.3333 13.687 13.3333 13.3333V5.33334L9.33333 1.33334Z"
                                    stroke="white"
                                    strokeWidth="1.33333"
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                />
                                <path
                                    d="M9.33333 1.33334V5.33334H13.3333"
                                    stroke="white"
                                    strokeWidth="1.33333"
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                />
                            </svg>
                            <span className={s.pdfName}>{inboxMessage.file?.fileName}</span>
                        </div>
                    </div>
                ))}
        </div>
    );
};

export default OpsInboxMessage;
